import { formatUnits } from '@ethersproject/units';
import { FC, useCallback, useEffect, useState } from 'react';
import useNotify from '../../../hooks/useNotify';
import useSiloRouter from '../../../hooks/useSiloRouter';
import {
  ActionForm,
  DoApprove,
  MutationVariables,
  QueryApproval,
  SubmitButtonContent,
  useActionFormContext,
  ValidateForm,
  UnstableOracleWarning,
} from '../common/ActionForm';
import { useThisDecoratedSilo } from '../silo';
import {
  DecoratedSilo,
  getCollateralAssets,
  getNonCollateralAssets,
  getNonDebtAssets,
  Recipe,
} from 'queries/decoratedSilo';
import { useRouterSelectedAddress } from '../common/ActionForm/useRouterSelectedAddress';
import { useClearAssetFromQueryString } from '../silo/hooks';
import {
  adjustToFullUSD,
  asDecimal,
  asFullUSD,
  handleParsedError,
  parseUserInitiatedTransactionError,
} from '@silo/utils';
import { GetNewLTV, HealthFactorConfig } from '../common/HealthFactor';
import produce from 'immer';
import { calculateLTV, getAssociatedAsset, mapToCollateralAsset } from 'app/features/util/ltv';
import { GetAverageAPY, MapAPYData } from '../common/ActionForm/ActionFormCharts';
import { calculateUtilization } from 'app/functions/silo/calculations';
import mean from 'lodash/mean';
import posthog from 'posthog-js';
import { GetNewUtilization } from '../../../hooks/useUtilizationImpact';
import { AssetBorrowInfo } from '../common/ActionForm/BorrowInfo';
import { useAddTransaction, useSiloWeb3 } from '@silo/web3';
import { useFinishTransaction } from '../silo/useFinishTransaction';
import { useIsAssetActionFrozen } from '../common/ActionForm/useIsFrozen';
import { GetMaxAmount } from 'app/hooks/useMaxTokenAmount';
import { isDisabledCollateral } from '../disabled-collateral/isDisabledCollateral';
import { CurveLpMessage } from './CurveLpMessage';
import { getMinDepositAmount } from 'app/config/networks';
import {
  AssetKeyLegacy,
  ChecksumAddress,
  ProtocolInstance,
  createBigNumberWithDecimals,
  makeAddressSync,
} from '@silo/types';
import { DisabledCollateralDescription } from '../disabled-collateral/DisabledCollateralDescription';
import { useIsCurveLpAsset } from 'app/hooks/useCurveLpAsset';
import { DisabledCollateralWarning } from '@silo/ui';
import { getHasUnstableOracle } from '@silo/config';

export type DoDeposit = (isBorrowable: boolean, variables: MutationVariables) => Promise<void>;

export const useDoDeposit = (): DoDeposit => {
  const decoratedSilo = useThisDecoratedSilo();
  const { routerDeposit } = useSiloRouter();
  const addTransaction = useAddTransaction();
  const { notifyError, notifyWarning } = useNotify();
  const finishTransaction = useFinishTransaction();
  const { siloAddress } = decoratedSilo;

  return useCallback<DoDeposit>(
    async (isBorrowable, { asset, amount, isFullAmount }) => {
      try {
        const { assetAddress } = asset;
        const tx = await routerDeposit(siloAddress, assetAddress, amount, !isBorrowable, asset.isCurveLpAsset);

        const formattedAmount = asDecimal(formatUnits(amount, asset.decimals));
        addTransaction(tx, `Deposit ${formattedAmount} ${asset.symbol}`);
        posthog.capture('deposit', { siloAddress, assetAddress, amount });

        const { status } = await tx.wait();

        if (status === 0) {
          throw new Error(`Transaction ${tx.hash} reverted`);
        }

        finishTransaction(getDepositRecipe(isBorrowable, { asset, amount, isFullAmount }));
      } catch (err) {
        console.error('Error on deposit:,', err);
        const parsed = parseUserInitiatedTransactionError(err);

        const captureAndNotify = (message: string) => {
          notifyError(message);
        };

        const { rethrow } = handleParsedError({
          onWarning: notifyWarning,
          onError: captureAndNotify,
          onUnparsed: captureAndNotify,
        })(parsed);

        // re-throw error to be handled by react-query
        if (rethrow) throw err;
      }
    },
    [routerDeposit, addTransaction, notifyError, siloAddress, notifyWarning, finishTransaction]
  );
};

type GetDepositRecipe = (...params: Parameters<DoDeposit>) => Recipe;

const getDepositRecipe: GetDepositRecipe =
  (isBorrowable, { asset, amount }) =>
  (draft) => {
    const assetToUpdate = draft.assets.find(({ assetAddress }) => asset.assetAddress === assetAddress);

    if (!assetToUpdate) return;

    const {
      collateralBalance: previousCollateralBalance,
      totalDeposits: previousTotalDeposits,
      collateralOnlyBalance: previousCollateralOnlyBalance,
      collateralOnlyDeposits: previousCollateralOnlyDeposits,
      userBalance: previousUserBalance,
    } = assetToUpdate;

    if (isBorrowable) {
      assetToUpdate.collateralBalance = previousCollateralBalance.add(amount);
      assetToUpdate.totalDeposits = previousTotalDeposits.add(amount);
    } else {
      assetToUpdate.collateralOnlyBalance = previousCollateralOnlyBalance.add(amount);
      assetToUpdate.collateralOnlyDeposits = previousCollateralOnlyDeposits.add(amount);
    }

    assetToUpdate.userBalance = previousUserBalance.sub(amount);
  };

const getMaxAmount: GetMaxAmount = ({ selectedAsset: { userBalance, decimals } }) => ({
  type: 'userBalance',
  value: createBigNumberWithDecimals(userBalance, decimals),
});

const validateForm: ValidateForm = ({ selectedAsset, normalizedTokenAmount, amountInUSD, protocolInstance }) => {
  if (selectedAsset.userBalance.isZero()) {
    return {
      message: 'Your balance is zero.',
      submitMessage: `Insufficient ${selectedAsset.symbol} balance`,
    };
  }

  if (normalizedTokenAmount?.gt(selectedAsset.userBalance)) {
    return {
      message: `You don't have enough ${selectedAsset.symbol}`,
      submitMessage: `Insufficient ${selectedAsset.symbol} balance`,
    };
  }

  const minDepositAmount = getMinDepositAmount(protocolInstance);

  if (amountInUSD && adjustToFullUSD(amountInUSD).bigNumber.toBigInt() < minDepositAmount.value) {
    return {
      message: `Minimum deposit amount is ${asFullUSD(minDepositAmount)}`,
    };
  }

  return null;
};

const getNewLTV: GetNewLTV = ({ currentLTV: { args }, normalizedTokenAmount, selectedAsset }) => {
  const newArgs = produce(args, (draft) => {
    const associatedAsset = getAssociatedAsset(draft.collateralAssets, selectedAsset);

    if (associatedAsset) {
      associatedAsset.balance += normalizedTokenAmount.toBigInt();
    } else {
      const newCollateralAsset = mapToCollateralAsset(selectedAsset);

      newCollateralAsset.balance = normalizedTokenAmount.toBigInt();

      draft.collateralAssets.push(newCollateralAsset);
    }
  });

  return calculateLTV(newArgs);
};

const healthFactorConfig: HealthFactorConfig = {
  getTokenAdjustment: ({ action, tokens }) => {
    if (action !== 'deposit') return 0n;

    return tokens;
  },
  getMax: ({ riskFactor }) => riskFactor,
};

export interface DepositFormProps {
  protocolInstance: ProtocolInstance;
  queryApproval: QueryApproval;
  doApprove: DoApprove;
  doDeposit: DoDeposit;
}

export const DepositForm: FC<DepositFormProps> = ({ protocolInstance, queryApproval, doApprove, doDeposit }) => {
  const { protocolKey } = useSiloWeb3();
  const decoratedSilo = useThisDecoratedSilo();
  const marketAddress = makeAddressSync(decoratedSilo.siloAddress);
  const hasUnstableOracle = getHasUnstableOracle({ protocolKey: protocolInstance, marketAddress });
  const isActionFrozen = useIsAssetActionFrozen('deposit');

  const [isBorrowable, setIsBorrowable] = useState(true);

  const doSubmit = useCallback(
    (variables: MutationVariables) => doDeposit(isBorrowable, variables),
    [doDeposit, isBorrowable]
  );

  const clearAssetFromQueryString = useClearAssetFromQueryString('deposit');

  /**
   * Only show assets that do not have a borrow position
   *
   * Select the first asset that has a deposit position if applicable
   */
  const assetsToDisplay = getNonDebtAssets(decoratedSilo);
  const collateralAssets = getCollateralAssets(decoratedSilo);
  const initialSelectedAddress =
    useRouterSelectedAddress('deposit') ?? collateralAssets[0]?.assetAddress ?? assetsToDisplay[0].assetAddress;
  const [assetKey, setAssetKey] = useState<AssetKeyLegacy>('base');

  const postChartPanels = [<AssetBorrowInfo />];

  return (
    <>
      <ActionForm
        protocolInstance={protocolInstance}
        assetsToDisplay={assetsToDisplay}
        getMaxAmount={getMaxAmount}
        queryStringSelectedAddress={initialSelectedAddress}
        maxLabelSlot="Wallet balance"
        validateForm={validateForm}
        postAmountSlot={<DepositAllAssetsWarning />}
        postMaxSlot={<PostMaxSlot setAssetKey={setAssetKey} />}
        preSubmitSlot={<PreSubmitSlot />}
        getNewLTV={getNewLTV}
        getNewUtilization={getNewUtilization}
        submitButtonContentSlot={({ selectedAsset }) => {
          return (
            <SubmitButtonContent
              submitCopy={
                isActionFrozen(selectedAsset.assetAddress, decoratedSilo.siloAddress)
                  ? `Depositing ${selectedAsset.symbol} is temporarily disabled`
                  : 'Deposit'
              }
              submittingCopy="Depositing"
            />
          );
        }}
        postChartPanels={postChartPanels}
        doSubmit={doSubmit}
        requireApproval={{
          queryApproval,
          doApprove,
        }}
        disableSubmit={({ selectedAsset }) => isActionFrozen(selectedAsset.assetAddress, decoratedSilo.siloAddress)}
        healthFactorConfig={healthFactorConfig}
        data-cy="deposit-form"
        submissionModalContentSlot={hasUnstableOracle ? <UnstableOracleWarning /> : undefined}
        onSelectedAddressChange={clearAssetFromQueryString}
        aprChartConfig={{
          mapAPYData,
          getAverageAPY,
        }}
        termsOfUseAction="deposit"
      />
    </>
  );
};

const getNewUtilization: GetNewUtilization = (amount, deposits, liquidity) => {
  return calculateUtilization(deposits + amount, deposits - liquidity);
};

const mapAPYData: MapAPYData = (data) => data.map((d) => ({ ...d, borrowApy: undefined }));

const getAverageAPY: GetAverageAPY = (data) => mean(data.map((d) => d.depositApy));

const shouldWarnAboutDepositingAllAssets = (
  decoratedSilo: DecoratedSilo,
  selectedAddress: ChecksumAddress
): boolean => {
  const nonCollateralAssets = getNonCollateralAssets(decoratedSilo);

  return nonCollateralAssets.length === 1 && nonCollateralAssets[0].assetAddress === selectedAddress;
};

const DepositAllAssetsWarning = () => {
  const decoratedSilo = useThisDecoratedSilo();
  const { selectedAddress } = useActionFormContext();

  if (!shouldWarnAboutDepositingAllAssets(decoratedSilo, selectedAddress)) return null;

  return (
    <p className="text-base text-white" data-cy="deposit-all-assets-warning">
      If you deposit all of this silo&apos;s assets, you won&apos;t be able to borrow against any of them.
    </p>
  );
};

interface PostMaxSlotProps {
  setAssetKey: (value: AssetKeyLegacy) => void;
}

const PostMaxSlot: FC<PostMaxSlotProps> = ({ setAssetKey }) => {
  const { siloAddress } = useThisDecoratedSilo();
  const { selectedAsset } = useActionFormContext();
  const isCurveLpAsset = useIsCurveLpAsset(siloAddress, selectedAsset.assetKey);
  useEffect(() => {
    setAssetKey(selectedAsset.assetKey);
  }, [selectedAsset.assetKey, setAssetKey]);

  if (isCurveLpAsset) {
    return <CurveLpMessage />;
  }

  return null;
};

const PreSubmitSlot: FC = () => {
  const { selectedAsset } = useActionFormContext();

  if (!isDisabledCollateral(selectedAsset.maximumLTV.toBigInt())) {
    return null;
  }

  return (
    <DisabledCollateralWarning
      assetSymbol={selectedAsset.symbol}
      variant="form"
      description={<DisabledCollateralDescription assetAddress={selectedAsset.assetAddress} />}
    />
  );
};
